import { useState } from 'react';
import {
  Container,
  Paper,
  Title,
  Stack,
  Group,
  Select,
  TextInput,
  Button,
  Table,
  Badge,
  Text,
  Loader,
  Center,
  Pagination,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconSearch, IconFilter, IconDownload } from '@tabler/icons-react';
import { format } from 'date-fns';
import { useOrganization } from '@/hooks/useOrganization';
import { useAuditLogs } from '@/hooks/api/useAuditLogs';
import { useOrganizationMembers } from '@/hooks/api/useOrganizationMembers';
import { useColumnVisibility, type ColumnDefinition } from '@/hooks/useColumnVisibility';
import { useTableSort } from '@/hooks/useTableSort';
import { ColumnVisibilityMenu } from '@/components/ui/ColumnVisibilityMenu';
import { SortableTableHeader } from '@/components/ui/SortableTableHeader';

const ACTION_TYPES = [
  { value: 'CREATE', label: 'Create' },
  { value: 'UPDATE', label: 'Update' },
  { value: 'DELETE', label: 'Delete' },
  { value: 'ASSIGN', label: 'Assign' },
  { value: 'UNASSIGN', label: 'Unassign' },
  { value: 'STATUS_CHANGE', label: 'Status Change' },
  { value: 'LOCATION_CHANGE', label: 'Location Change' },
  { value: 'AUDIT', label: 'Audit' },
  { value: 'MAINTENANCE', label: 'Maintenance' },
  { value: 'DOCUMENT_UPLOAD', label: 'Document Upload' },
  { value: 'DOCUMENT_DELETE', label: 'Document Delete' },
  { value: 'BULK_UPDATE', label: 'Bulk Update' },
];

const getActionColor = (actionType: string) => {
  switch (actionType) {
    case 'CREATE':
      return 'green';
    case 'DELETE':
      return 'red';
    case 'UPDATE':
      return 'blue';
    case 'ASSIGN':
    case 'UNASSIGN':
      return 'violet';
    case 'STATUS_CHANGE':
      return 'orange';
    case 'LOCATION_CHANGE':
      return 'teal';
    case 'AUDIT':
      return 'cyan';
    case 'MAINTENANCE':
      return 'yellow';
    case 'DOCUMENT_UPLOAD':
    case 'DOCUMENT_DELETE':
      return 'grape';
    default:
      return 'gray';
  }
};

type AuditLogColumnKey = 'date' | 'action' | 'summary' | 'field' | 'changes' | 'user';

const AUDIT_LOG_COLUMNS: ColumnDefinition<AuditLogColumnKey>[] = [
  { key: 'date', label: 'Date', sortable: true, defaultVisible: true },
  { key: 'action', label: 'Action', sortable: true, defaultVisible: true },
  { key: 'summary', label: 'Summary', sortable: true, defaultVisible: true },
  { key: 'field', label: 'Field', sortable: true, defaultVisible: true },
  { key: 'changes', label: 'Changes', sortable: false, defaultVisible: true },
  { key: 'user', label: 'User', sortable: true, defaultVisible: true },
];

export function ActivityLogPage() {
  const { organizationId } = useOrganization();
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState<string | null>(null);
  const [userFilter, setUserFilter] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [majorEventsOnly, setMajorEventsOnly] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 50;

  // Column visibility
  const columnVisibility = useColumnVisibility({
    cookieName: 'audit-logs-table-columns',
    columns: AUDIT_LOG_COLUMNS,
  });

  const { data: members = [] } = useOrganizationMembers(organizationId || '');

  const { data: logs = [], isLoading } = useAuditLogs(organizationId || '', {
    actionType: actionFilter || undefined,
    userId: userFilter || undefined,
    startDate: dateRange[0] ? dateRange[0].toISOString() : undefined,
    endDate: dateRange[1] ? dateRange[1].toISOString() : undefined,
    majorEventsOnly,
  });

  // Client-side filtering for search
  const filteredLogs = logs.filter((log) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      log.change_summary?.toLowerCase().includes(searchLower) ||
      log.field_changed?.toLowerCase().includes(searchLower) ||
      log.old_value?.toLowerCase().includes(searchLower) ||
      log.new_value?.toLowerCase().includes(searchLower) ||
      log.user_name?.toLowerCase().includes(searchLower)
    );
  });

  // Sorting
  const { sortedData, sortKey, sortDirection, handleSort } = useTableSort({
    data: filteredLogs,
  });

  // Pagination
  const totalPages = Math.ceil(sortedData.length / pageSize);
  const paginatedLogs = sortedData.slice((page - 1) * pageSize, page * pageSize);

  const handleExportCSV = () => {
    const headers = ['Date', 'Action', 'Asset', 'Field', 'Old Value', 'New Value', 'Summary', 'User'];
    const rows = filteredLogs.map((log) => [
      format(new Date(log.created_at), 'yyyy-MM-dd HH:mm:ss'),
      log.action_type,
      log.asset_id || '',
      log.field_changed || '',
      log.old_value || '',
      log.new_value || '',
      log.change_summary,
      log.user_name || 'System',
    ]);

    const csv = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit-logs-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setActionFilter(null);
    setUserFilter(null);
    setDateRange([null, null]);
    setMajorEventsOnly(false);
    setPage(1);
  };

  return (
    <Container fluid py="xl">
      <Stack gap="lg">
        <Group justify="apart">
          <div>
            <Title order={1}>Activity Log</Title>
            <Text size="sm" c="dimmed" mt={4}>
              Complete activity trail of all changes across your organization
            </Text>
          </div>
          <Button
            leftSection={<IconDownload size={16} />}
            onClick={handleExportCSV}
            disabled={filteredLogs.length === 0}
          >
            Export CSV
          </Button>
        </Group>

        <Paper p="md" withBorder>
          <Stack gap="md">
            <Text size="sm" fw={500}>
              Filters
            </Text>

            <Group>
              <TextInput
                placeholder="Search logs..."
                leftSection={<IconSearch size={16} />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ flex: 1 }}
              />
              <Select
                placeholder="Action Type"
                leftSection={<IconFilter size={16} />}
                data={[{ value: '', label: 'All Actions' }, ...ACTION_TYPES]}
                value={actionFilter}
                onChange={setActionFilter}
                clearable
                style={{ width: 200 }}
              />
              <Select
                placeholder="User"
                data={[
                  { value: '', label: 'All Users' },
                  ...members.map((member) => ({
                    value: member.user_id,
                    label: member.profile?.full_name || member.profile?.email || 'Unknown User',
                  })),
                ]}
                value={userFilter}
                onChange={setUserFilter}
                clearable
                searchable
                style={{ width: 200 }}
              />
            </Group>

            <Group>
              <DatePickerInput
                type="range"
                label="Date Range"
                placeholder="Pick date range"
                value={dateRange}
                onChange={setDateRange}
                clearable
                style={{ flex: 1 }}
              />
              <Select
                label="Event Type"
                data={[
                  { value: 'all', label: 'All Events' },
                  { value: 'major', label: 'Major Events Only' },
                ]}
                value={majorEventsOnly ? 'major' : 'all'}
                onChange={(value) => setMajorEventsOnly(value === 'major')}
                style={{ width: 200 }}
              />
            </Group>

            <Group justify="space-between">
              <ColumnVisibilityMenu
                columns={AUDIT_LOG_COLUMNS}
                visibleColumns={columnVisibility.visibleColumns}
                onToggleColumn={columnVisibility.toggleColumn}
                onShowAll={columnVisibility.showAllColumns}
                onResetDefaults={columnVisibility.resetToDefaults}
              />
              <Button variant="subtle" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            </Group>
          </Stack>
        </Paper>

        <Paper p="md" withBorder>
          <Stack gap="md">
            <Group justify="apart">
              <Text size="sm" fw={500}>
                Results: {filteredLogs.length} logs
              </Text>
            </Group>

            {isLoading ? (
              <Center h={400}>
                <Loader />
              </Center>
            ) : filteredLogs.length === 0 ? (
              <Center h={400}>
                <Text c="dimmed">No audit logs found</Text>
              </Center>
            ) : (
              <>
                <Table.ScrollContainer minWidth={800}>
                <Table highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      {columnVisibility.isColumnVisible('date') && (
                        <SortableTableHeader
                          label="Date"
                          sortKey="created_at"
                          currentSortKey={sortKey}
                          currentDirection={sortDirection}
                          onSort={handleSort}
                        />
                      )}
                      {columnVisibility.isColumnVisible('action') && (
                        <SortableTableHeader
                          label="Action"
                          sortKey="action_type"
                          currentSortKey={sortKey}
                          currentDirection={sortDirection}
                          onSort={handleSort}
                        />
                      )}
                      {columnVisibility.isColumnVisible('summary') && (
                        <SortableTableHeader
                          label="Summary"
                          sortKey="change_summary"
                          currentSortKey={sortKey}
                          currentDirection={sortDirection}
                          onSort={handleSort}
                        />
                      )}
                      {columnVisibility.isColumnVisible('field') && (
                        <SortableTableHeader
                          label="Field"
                          sortKey="field_changed"
                          currentSortKey={sortKey}
                          currentDirection={sortDirection}
                          onSort={handleSort}
                        />
                      )}
                      {columnVisibility.isColumnVisible('changes') && <Table.Th>Changes</Table.Th>}
                      {columnVisibility.isColumnVisible('user') && (
                        <SortableTableHeader
                          label="User"
                          sortKey="user_name"
                          currentSortKey={sortKey}
                          currentDirection={sortDirection}
                          onSort={handleSort}
                        />
                      )}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedLogs.map((log) => (
                      <Table.Tr key={log.id}>
                        {columnVisibility.isColumnVisible('date') && (
                          <Table.Td>
                            <Text size="sm">
                              {format(new Date(log.created_at), 'MMM dd, yyyy')}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {format(new Date(log.created_at), 'h:mm a')}
                            </Text>
                          </Table.Td>
                        )}
                        {columnVisibility.isColumnVisible('action') && (
                          <Table.Td>
                            <Group gap="xs">
                              <Badge size="sm" color={getActionColor(log.action_type)}>
                                {log.action_type.replace('_', ' ')}
                              </Badge>
                              {log.is_major_event && (
                                <Badge size="xs" variant="light" color="red">
                                  Major
                                </Badge>
                              )}
                            </Group>
                          </Table.Td>
                        )}
                        {columnVisibility.isColumnVisible('summary') && (
                          <Table.Td>
                            <Text size="sm">{log.change_summary}</Text>
                          </Table.Td>
                        )}
                        {columnVisibility.isColumnVisible('field') && (
                          <Table.Td>
                            <Text size="sm" c="dimmed">
                              {log.field_changed || '-'}
                            </Text>
                          </Table.Td>
                        )}
                        {columnVisibility.isColumnVisible('changes') && (
                          <Table.Td>
                            {log.old_value && log.new_value ? (
                              <Stack gap={4}>
                                <Group gap="xs">
                                  <Badge size="xs" variant="light" color="red">
                                    {log.old_value}
                                  </Badge>
                                  <Text size="xs" c="dimmed">
                                    →
                                  </Text>
                                  <Badge size="xs" variant="light" color="green">
                                    {log.new_value}
                                  </Badge>
                                </Group>
                              </Stack>
                            ) : (
                              <Text size="sm" c="dimmed">
                                -
                              </Text>
                            )}
                          </Table.Td>
                        )}
                        {columnVisibility.isColumnVisible('user') && (
                          <Table.Td>
                            <Text size="sm">{log.user_name || 'System'}</Text>
                          </Table.Td>
                        )}
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
                </Table.ScrollContainer>

                {totalPages > 1 && (
                  <Group justify="center" mt="md">
                    <Pagination value={page} onChange={setPage} total={totalPages} />
                  </Group>
                )}
              </>
            )}
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
}
